import { IconName } from '../components/atoms/Icon/Icon.types'

interface Feature {
  icon: IconName
  text: string
}

/**
 * Comprehensive feature icon mapping
 * Maps normalized feature keys to appropriate icons
 */
const featureIconMap: Record<string, IconName> = {
  // Outdoor spaces & Views
  balconies: 'sun',
  balcony: 'sun',
  terraces: 'sun',
  terrace: 'sun',
  garden: 'leaf',
  gardenhouse: 'leaf',
  conservatory: 'leaf',
  playground: 'users',
  porches: 'home',
  view: 'eye',
  mountainview: 'mountain',
  lakeview: 'waves',

  // Parking & Storage
  parkingoutdoor: 'car',
  parkingindoor: 'garage',
  'parkingspace/garage': 'garage',
  garage: 'garage',
  parking: 'car',
  parkingspace: 'car',
  cellar: 'box',
  attic: 'box',
  tinkerroom: 'tool',
  storageroom: 'box',
  pantry: 'box',

  // Building features & Equipment
  lift: 'arrow-up',
  elevator: 'arrow-up',
  liftingplatform: 'arrow-up',
  ramp: 'arrow-up',
  fireplace: 'fire',
  woodstove: 'fire',
  heating: 'fire',
  aircondition: 'wind',
  airconditioning: 'wind',
  sauna: 'water',
  swimmingpools: 'water',
  swimmingpool: 'water',
  pool: 'water',

  // Kitchen & Appliances
  washingmachine: 'water',
  dryer: 'wind',
  dishwasher: 'water',
  openkitchen: 'home',
  equippedkitchen: 'tool',
  separatedkitchen: 'grid',

  // Bathroom features
  baths: 'bath',
  bathtubs: 'bath',
  showers: 'water',
  toilets: 'water',
  restrooms: 'water',

  // Connectivity & Modern amenities
  cabletv: 'wifi',
  opticalfiber: 'wifi',
  fiberoptic: 'wifi',
  watersupply: 'water',

  // Building characteristics
  newbuilding: 'star',
  oldbuilding: 'clock',
  minergiecertified: 'check-circle',
  firsttimeuse: 'star',
  buildinglawconnected: 'link',
  underbuildinglaws: 'shield',

  // Layout & Architecture
  cornerhouse: 'home',
  middlehouse: 'home',
  underroof: 'home',
  raisedgroundfloor: 'arrow-up',
  brightly: 'sun',
  sunny: 'sun',
  quiet: 'moon',
  central: 'location',

  // Accessibility & Social
  wheelchairaccessible: 'users',
  childfriendly: 'users',
  petsallowed: 'heart',
  flatshare: 'users',

  // Furnishing & Move-in
  furnished: 'sofa',

  // Special eligibility
  secondaryresidenceeligible: 'key',
}

/**
 * Special text formatting for camelCase features
 */
const textFormatMap: Record<string, string> = {
  ParkingOutdoor: 'Outdoor Parking',
  ParkingIndoor: 'Indoor Parking',
  CableTV: 'Cable TV',
  ChildFriendly: 'Child Friendly',
  WheelchairAccessible: 'Wheelchair Accessible',
  PetsAllowed: 'Pets Allowed',
  NewBuilding: 'New Building',
  OldBuilding: 'Old Building',
  MinergieCertified: 'Minergie Certified',
  FlatShare: 'Flat Share',
  WaterSupply: 'Water Supply',
  RaisedGroundFloor: 'Raised Ground Floor',
  SwimmingPools: 'Swimming Pool',
  SwimmingPool: 'Swimming Pool',
  FirstTimeUse: 'First Time Use',
  Tinkerroom: 'Tinker Room',
  UnderRoof: 'Under Roof',
  CornerHouse: 'Corner House',
  WoodStove: 'Wood Stove',
  BuildingLandConnected: 'Building Land Connected',
  Gardenhouse: 'Garden House',
  MiddleHouse: 'Middle House',
  LiftingPlatform: 'Lifting Platform',
  UnderBuildingLaws: 'Under Building Laws',
  Washingmachine: 'Washing Machine',
  AirConditioning: 'Air Conditioning',
  OpenKitchen: 'Open Kitchen',
  EquippedKitchen: 'Equipped Kitchen',
  SeparatedKitchen: 'Separated Kitchen',
  StorageRoom: 'Storage Room',
  OpticalFiber: 'Optical Fiber',
  FiberOptic: 'Fiber Optic',
  SecondaryResidenceEligible: 'Secondary Residence Eligible',
}

/**
 * Normalizes a feature string for lookup
 */
const normalizeFeature = (feature: string): string => {
  return feature.toLowerCase().replace(/[\s\-_/]+/g, '')
}

/**
 * Gets the icon for a feature
 */
const getFeatureIcon = (feature: string): IconName => {
  const normalized = normalizeFeature(feature)
  return featureIconMap[normalized] || 'check'
}

/**
 * Gets the display text for a feature
 */
const getFeatureText = (
  feature: string,
  translations?: Record<string, string>
): string => {
  const normalized = normalizeFeature(feature)

  // Check translations first (skip empty strings)
  if (translations?.[normalized]?.trim()) {
    return translations[normalized]
  }

  // Check special text formatting
  if (textFormatMap[feature]) {
    return textFormatMap[feature]
  }

  // Default: capitalize first letter of each word
  return feature
    .replace(/([a-z])([A-Z])/g, '$1 $2') // Split camelCase
    .split(/[\s_-]+/)
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
    .join(' ')
}

/**
 * Format features array for display
 * @param features Array of feature strings
 * @param translations Optional translation map (normalized key -> translated text)
 */
export const formatFeatures = (
  features?: string[],
  translations?: Record<string, string>
): Feature[] => {
  if (!features || features.length === 0) return []

  return features
    .filter((feature) => feature?.trim())
    .map((feature) => ({
      icon: getFeatureIcon(feature),
      text: getFeatureText(feature, translations),
    }))
    .filter((f) => f.text.trim())
}

/**
 * Gets the normalized key for a feature (useful for building translation maps)
 */
export const getFeatureKey = (feature: string): string => {
  return normalizeFeature(feature)
}
